import { useEffect, useState } from "react";
import { Paper, Typography, Button, TextField } from "@mui/material";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import QRCode from "qrcode";
import { useLanguage } from '../../context/LanguageContext';
import api from '../../api/api';
import "../../assets/style/Security.css";

export default function MultiFactorAuth() {
  const { t } = useLanguage();
  const [emailAuthStatus, setEmailAuthStatus] = useState({
    email_2fa_enabled: false,
    email_2fa_pending: false,
  });
  const [emailAuthLoading, setEmailAuthLoading] = useState(false);
  const [emailAuthMessage, setEmailAuthMessage] = useState("");
  const [emailAuthError, setEmailAuthError] = useState("");
  const [authenticatorStatus, setAuthenticatorStatus] = useState({
    google_authenticator_enabled: false,
    google_authenticator_pending: false,
  });
  const [authenticatorSetup, setAuthenticatorSetup] = useState(null);
  const [authenticatorQr, setAuthenticatorQr] = useState("");
  const [authenticatorCode, setAuthenticatorCode] = useState("");
  const [authenticatorLoading, setAuthenticatorLoading] = useState(false);
  const [authenticatorMessage, setAuthenticatorMessage] = useState("");
  const [authenticatorError, setAuthenticatorError] = useState("");

  const loadEmailAuthStatus = async () => {
    try {
      const response = await api.get('/email-authentication/status');
      setEmailAuthStatus(response.data);
    } catch (error) {
      setEmailAuthError(error.response?.data?.message || t('emailAuthStatusFailed'));
    }
  };

  const loadAuthenticatorStatus = async () => {
    try {
      const response = await api.get('/authenticator-app/status');
      setAuthenticatorStatus(response.data);
    } catch (error) {
      setAuthenticatorError(error.response?.data?.message || t('authenticatorStatusFailed'));
    }
  };

  useEffect(() => {
    loadEmailAuthStatus();
    loadAuthenticatorStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuthenticatorSetup = async () => {
    if (authenticatorStatus.google_authenticator_enabled) {
      return;
    }

    setAuthenticatorLoading(true);
    setAuthenticatorMessage("");
    setAuthenticatorError("");
    setAuthenticatorCode("");

    try {
      const response = await api.post('/authenticator-app/setup');
      setAuthenticatorStatus(response.data);
      setAuthenticatorSetup(response.data);

      if (response.data.otpauth_uri) {
        const qrDataUrl = await QRCode.toDataURL(response.data.otpauth_uri, {
          width: 180,
          margin: 1,
          color: {
            dark: '#0a1f10',
            light: '#ffffff',
          },
        });
        setAuthenticatorQr(qrDataUrl);
      }

      setAuthenticatorMessage(response.data.message || t('authenticatorSetupReady'));
    } catch (error) {
      setAuthenticatorError(error.response?.data?.message || t('authenticatorSetupFailed'));
    } finally {
      setAuthenticatorLoading(false);
    }
  };

  const handleAuthenticatorEnable = async () => {
    setAuthenticatorLoading(true);
    setAuthenticatorMessage("");
    setAuthenticatorError("");

    try {
      const response = await api.post('/authenticator-app/enable', {
        code: authenticatorCode,
      });

      setAuthenticatorStatus(response.data);
      setAuthenticatorSetup(null);
      setAuthenticatorQr("");
      setAuthenticatorCode("");
      setAuthenticatorMessage(response.data.message || t('authenticatorEnabled'));
    } catch (error) {
      setAuthenticatorError(error.response?.data?.message || t('authenticatorEnableFailed'));
    } finally {
      setAuthenticatorLoading(false);
    }
  };

  const handleEmailAuthAction = async () => {
    setEmailAuthLoading(true);
    setEmailAuthMessage("");
    setEmailAuthError("");

    try {
      const endpoint = emailAuthStatus.email_2fa_enabled
        ? '/email-authentication/disable'
        : '/email-authentication/setup';

      const response = await api.post(endpoint);
      setEmailAuthStatus(response.data);
      setEmailAuthMessage(response.data.message || (emailAuthStatus.email_2fa_enabled ? t('emailAuthDisabled') : t('emailAuthEmailSent')));
    } catch (error) {
      setEmailAuthError(error.response?.data?.message || t('emailAuthActionFailed'));
    } finally {
      setEmailAuthLoading(false);
    }
  };

  const emailStatusLabel = emailAuthStatus.email_2fa_enabled
    ? t('on')
    : emailAuthStatus.email_2fa_pending
      ? t('pending')
      : t('off');

  const emailStatusClass = emailAuthStatus.email_2fa_enabled
    ? 'status-on'
    : emailAuthStatus.email_2fa_pending
      ? 'status-pending'
      : 'status-off';

  const authenticatorStatusLabel = authenticatorStatus.google_authenticator_enabled
    ? t('on')
    : authenticatorStatus.google_authenticator_pending
      ? t('pending')
      : t('off');

  const authenticatorStatusClass = authenticatorStatus.google_authenticator_enabled
    ? 'status-on'
    : authenticatorStatus.google_authenticator_pending
      ? 'status-pending'
      : 'status-off';

  return (
    <div id="multi-factor-authentication" className="mfa-section">
      <Typography variant="h6" className="card-title">
        {t('multiFactorAuthentication')}
      </Typography>
      <div className="mfa-cards-container">
        <Paper elevation={0} className="card security-card mfa-card">
          <div className="mfa-status">
            <span className={`status-dot ${authenticatorStatusClass}`}></span> {authenticatorStatusLabel}
          </div>
          <Typography className="mfa-title">{t('authenticatorApp')}</Typography>
          <Typography className="mfa-description">
            {t('authenticatorAppDesc')}
          </Typography>
          <Button
            className="setup-button setup-button--wide"
            loading={authenticatorLoading}
            disabled={authenticatorLoading || authenticatorStatus.google_authenticator_enabled}
            onClick={handleAuthenticatorSetup}
          >
            {authenticatorStatus.google_authenticator_enabled ? t('enabled') : authenticatorStatus.google_authenticator_pending ? t('continueSetup') : t('setUp')}
          </Button>

          {authenticatorSetup && !authenticatorStatus.google_authenticator_enabled && (
            <div className="authenticator-setup">
              {authenticatorQr && (
                <img
                  src={authenticatorQr}
                  alt="Google Authenticator QR"
                  className="authenticator-qr"
                />
              )}
              <Typography className="authenticator-secret-label">{t('manualSetupKey')}</Typography>
              <Typography className="authenticator-secret">{authenticatorSetup.secret}</Typography>
              <TextField
                size="small"
                className="authenticator-code-input"
                label={t('authenticationCode')}
                value={authenticatorCode}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                onChange={(event) => setAuthenticatorCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              />
              <Button
                className="setup-button setup-button--wide authenticator-enable-button"
                loading={authenticatorLoading}
                disabled={authenticatorLoading || authenticatorCode.length !== 6}
                onClick={handleAuthenticatorEnable}
              >
                {t('enableAuthenticator')}
              </Button>
            </div>
          )}

          {authenticatorMessage && <Typography className="mfa-message mfa-message--success">{authenticatorMessage}</Typography>}
          {authenticatorError && <Typography className="mfa-message mfa-message--error">{authenticatorError}</Typography>}
        </Paper>

        <Paper elevation={0} className="card security-card mfa-card">
          <div className="mfa-status">
            <span className={`status-dot ${emailStatusClass}`}></span> {emailStatusLabel}
          </div>
          <Typography className="mfa-title">{t('emailAuthentication')}</Typography>
          <Typography className="mfa-description">
            {t('emailAuthenticationDesc')}
          </Typography>
          <Button
            className="setup-button setup-button--wide"
            loading={emailAuthLoading}
            disabled={emailAuthLoading}
            onClick={handleEmailAuthAction}
          >
            {emailAuthStatus.email_2fa_enabled ? t('disable') : emailAuthStatus.email_2fa_pending ? t('resendEmail') : t('setUp')}
          </Button>
          {emailAuthMessage && <Typography className="mfa-message mfa-message--success">{emailAuthMessage}</Typography>}
          {emailAuthError && <Typography className="mfa-message mfa-message--error">{emailAuthError}</Typography>}
        </Paper>
      </div>
      <div className="mfa-cards-container__bot tip-box">
        <LightbulbOutlinedIcon className="tip-icon" />
        <Typography variant="caption"> {t('mfaTip')} </Typography>
      </div>
    </div>
  );
}
